import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Building } from '../../interfaces/api/building';
import { BuildingFilterParameters } from '../../api/buildings';
import { fetchBuildings } from '../services/building.service';

interface BuildingState {
	buildings: {
		byId: Record<number, Building>;
		all: Building[];
	};
	selectedBuilding: Building | undefined;
	isLoading: boolean;
}

const initialState: BuildingState = {
	buildings: {
		byId: {},
		all: [],
	},
	selectedBuilding: undefined,
	isLoading: false,
};

export const fetchBuildingsThunk = createAsyncThunk<
	Building[],
	{ params: BuildingFilterParameters }
>('building/fetchBuildings', async ({ params }, { rejectWithValue }) => {
	try {
		return await fetchBuildings(params);
	} catch (error) {
		return rejectWithValue(error);
	}
});

const buildingSlice = createSlice({
	name: 'building',
	initialState,
	reducers: {
		selectBuilding: (state, action: PayloadAction<number>) => {
			state.selectedBuilding = state.buildings.byId[action.payload];
			localStorage.setItem('selectedBuilding', String(state.buildings.byId[action.payload].id));
		},
		unSelectBuilding: (state) => {
			state.selectedBuilding = undefined;
			localStorage.removeItem('selectedBuilding');
		},
		resetBuilding: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchBuildingsThunk.fulfilled, (state, action) => {
			const byId: Record<number, Building> = {};
			action.payload.forEach((building) => {
				byId[building.id] = building;
			});
			const all = action.payload.map((building) => building);
			state.buildings = { byId, all };
			const selectedStorageId = localStorage.getItem('selectedBuilding');
			if (selectedStorageId && byId[Number(selectedStorageId)]) {
				state.selectedBuilding = byId[Number(selectedStorageId)];
			}
			state.isLoading = false;
		});
		builder.addCase(fetchBuildingsThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchBuildingsThunk.rejected, (state, _action) => {
			state.buildings = {
				byId: {},
				all: [],
			};
			state.isLoading = false;
		});
	},
});

export const { selectBuilding, unSelectBuilding, resetBuilding } = buildingSlice.actions;

export default buildingSlice;
